import React, { Component } from "react";
import Swal from "sweetalert2";
import ICON from "../icons.js";
import DatePicker from "../../../components/DatePicker.js";
import { normalizePhone, PHONE_PATTERN, todayIso } from "../../../components/sale-format.js";

/* ============================================================================
 * HomeInquiry — ฟอร์มนัดดูห้อง (โมดัล)
 *
 * ★ backend บังคับว่าคำขอต้องผูกกับโครงการเสมอ ไม่งั้นคำขอจะเข้าหลังบ้านผิดโครงการ
 *   กดจากการ์ดห้อง = ใช้โครงการของห้องนั้น
 *   กดจากปุ่มติดต่อทั่วไปตอนดูรวมทุกโครงการ = ต้องให้ผู้ใช้เลือกโครงการก่อน
 *
 * ★ preview : หน้าตัวอย่างประกาศของผู้ดูแล (/room?preview=1) ใช้ข้อมูลสมมติ
 *   ส่งจริงไม่ได้อยู่แล้ว (ไม่มีโครงการ/ห้อง "preview" ใน DB) จึงปิดปุ่มส่งและบอกเหตุผลตรง ๆ
 *   แทนการปล่อยให้กดแล้วเจอ error ที่ผู้ดูแลอ่านไม่เข้าใจ
 *
 * ★ การเข้าถึง : role="dialog" + aria-modal · Esc ปิด · Tab วนอยู่ในโมดัล
 *   เปิดแล้วโฟกัสช่องแรก ปิดแล้วคืนโฟกัสให้ปุ่มที่กดเปิด · ทุก label ผูก input ด้วย htmlFor
 * ========================================================================== */
const EMPTY_FORM = { full_name: "", phone: "", email: "", interest_type: "สนใจซื้อ", visit_date: "", visit_time: "", message: "" };

export default class HomeInquiry extends Component {
    state = {
        form: { ...EMPTY_FORM },
        project_id: "",
        sending: false,
        error: "",
    };

    panelRef = React.createRef();

    componentDidMount() {
        if (this.props.open) this.onOpen();
    }

    componentDidUpdate(prevProps) {
        // เปิดโมดัลรอบใหม่ = ล้างของเก่าทิ้ง ไม่ให้ค้างข้อความ "ส่งสำเร็จ" จากรอบก่อน
        if (!prevProps.open && this.props.open) this.onOpen();
        if (prevProps.open && !this.props.open) this.onClosed();
    }

    componentWillUnmount() {
        document.removeEventListener("keydown", this.onKeyDown);
    }

    onOpen = () => {
        const { listing, project } = this.props;
        this.returnFocus = document.activeElement;
        document.addEventListener("keydown", this.onKeyDown);
        this.setState(
            {
                form: {
                    ...EMPTY_FORM,
                    interest_type: listing && listing.listing_type === "เช่า" ? "สนใจเช่า" : "สนใจซื้อ",
                },
                project_id: listing?.project_id || project?.project_id || "",
                sending: false,
                error: "",
            },
            () => {
                const first = this.panelRef.current && this.panelRef.current.querySelector("select, input");
                if (first) first.focus();
            }
        );
    };

    onClosed = () => {
        document.removeEventListener("keydown", this.onKeyDown);
        if (this.returnFocus && typeof this.returnFocus.focus === "function") this.returnFocus.focus();
    };

    close = () => this.props.onClose && this.props.onClose();

    onKeyDown = (event) => {
        if (!this.props.open) return;
        if (event.key === "Escape") {
            this.close();
            return;
        }
        // Tab วนอยู่ในโมดัล — ไม่หลุดไปโฟกัสเนื้อหาด้านหลังที่มองไม่เห็น
        if (event.key === "Tab" && this.panelRef.current) {
            const nodes = Array.from(
                this.panelRef.current.querySelectorAll("button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href]")
            );
            if (!nodes.length) return;
            const first = nodes[0];
            const last = nodes[nodes.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        }
    };

    set = (key, value) => this.setState((prev) => ({ form: { ...prev.form, [key]: value } }));

    send = async () => {
        const { form, project_id } = this.state;
        const { listing, onSubmit, preview } = this.props;
        if (preview) return;

        if (!project_id) return this.setState({ error: "กรุณาเลือกโครงการ" });
        if (!form.full_name.trim()) return this.setState({ error: "กรุณากรอกชื่อผู้ติดต่อ" });
        if (!form.phone.trim()) return this.setState({ error: "กรุณากรอกเบอร์โทรศัพท์" });
        // เบอร์ในไทยมี 9 หลัก (เบอร์บ้านบางพื้นที่) ถึง 10 หลัก ขึ้นต้นด้วย 0 — รูปแบบเดียวกับที่ backend ตรวจ
        if (!PHONE_PATTERN.test(form.phone)) return this.setState({ error: "เบอร์โทรศัพท์ต้องขึ้นต้นด้วย 0 และมี 9-10 หลัก" });
        // ★ กันวันในอดีตซ้ำอีกชั้น — DatePicker ไม่ให้เลือกอยู่แล้ว แต่เปิดโมดัลค้างข้ามคืนได้
        if (form.visit_date && form.visit_date < todayIso()) return this.setState({ error: "วันที่เข้าชมต้องไม่ใช่วันที่ผ่านมาแล้ว" });

        this.setState({ sending: true, error: "" });
        const result = await onSubmit({ project_id, listing_uid: listing ? listing.uid : "", ...form });
        this.setState({ sending: false, error: result.ok ? "" : result.message });

        if (!result.ok) return;

        // ★ ใช้ SweetAlert ชุดเดียวกับที่ระบบใช้อยู่ แทนหน้าจอ "ส่งเรียบร้อย" ที่ทำเอง
        //   ปิดโมดัลก่อนเด้ง ไม่งั้นผู้ใช้ต้องกดปิด 2 ชั้น
        this.close();
        await Swal.fire({
            icon: "success",
            title: "ส่งข้อมูลเรียบร้อย",
            text: "เจ้าหน้าที่จะติดต่อกลับตามเบอร์ที่ท่านให้ไว้",
            confirmButtonText: "ปิด",
            confirmButtonColor: "#198754",
        });
    };

    render() {
        const { open, listing, projects = [], preview } = this.props;
        const { form, project_id, sending, error } = this.state;
        if (!open) return null;

        return (
            <div className="hm-modal" onClick={this.close}>
                <div
                    className="hm-modal__panel"
                    ref={this.panelRef}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="hm-inquiry-title"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="hm-modal__head">
                        <div>
                            <h3 className="hm-modal__title" id="hm-inquiry-title">นัดดูห้อง</h3>
                            <small className="hm-muted">
                                {listing ? `ห้อง ${listing.address_number} · ${listing.project_name || ""}` : "สอบถามข้อมูลโครงการ"}
                            </small>
                        </div>
                        <button type="button" className="hm-modal__close" onClick={this.close} aria-label="ปิด">
                            <span className="hm-ico">{ICON.close}</span>
                        </button>
                    </div>

                    {preview && (
                        <div className="hm-alert hm-alert--info" role="note">
                            หน้าตัวอย่าง (ข้อมูลทดสอบ) — ส่งฟอร์มจริงไม่ได้ ใช้ดูหน้าตาฟอร์มเท่านั้น
                        </div>
                    )}

                    {!!error && (
                        <div className="hm-alert" role="alert">
                            {error}
                        </div>
                    )}

                    {/* ไม่ได้กดมาจากห้อง และดูรวมทุกโครงการอยู่ — ต้องเลือกโครงการเอง */}
                    {!listing && (
                        <div className="hm-form__row">
                            <label htmlFor="hm-inq-project">โครงการ *</label>
                            <select id="hm-inq-project" value={project_id} onChange={(e) => this.setState({ project_id: e.target.value })}>
                                <option value="">เลือกโครงการ</option>
                                {projects.map((p) => (
                                    <option key={p.project_id} value={p.project_id}>{p.project_name}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="hm-form__row">
                        <label htmlFor="hm-inq-name">ชื่อ-นามสกุล / ชื่อเล่น *</label>
                        <input id="hm-inq-name" autoComplete="name" value={form.full_name} onChange={(e) => this.set("full_name", e.target.value)} />
                    </div>
                    <div className="hm-form__row">
                        <label htmlFor="hm-inq-phone">เบอร์โทรศัพท์ *</label>
                        {/* ★ เบอร์โทร : normalize ก่อนแล้วค่อยตัด 10 หลัก (+66 812345678 → 0812345678)
                            ไม่ใส่ maxLength ที่ช่อง — วาง "+66 81 234 5678" (15 ตัวอักษร) จะโดนตัดก่อนถึง onChange */}
                        <input
                            id="hm-inq-phone"
                            type="tel"
                            inputMode="tel"
                            autoComplete="tel"
                            placeholder="0812345678"
                            value={form.phone}
                            onChange={(e) => this.set("phone", normalizePhone(e.target.value).slice(0, 10))}
                        />
                    </div>
                    <div className="hm-form__row">
                        <label htmlFor="hm-inq-email">อีเมล / Line</label>
                        <input id="hm-inq-email" value={form.email} onChange={(e) => this.set("email", e.target.value)} />
                    </div>
                    <div className="hm-form__row">
                        <label htmlFor="hm-inq-interest">ความสนใจ</label>
                        <select id="hm-inq-interest" value={form.interest_type} onChange={(e) => this.set("interest_type", e.target.value)}>
                            <option value="สนใจซื้อ">สนใจซื้อ</option>
                            <option value="สนใจเช่า">สนใจเช่า</option>
                        </select>
                    </div>
                    <div className="hm-form__grid">
                        <div className="hm-form__row">
                            <label htmlFor="hm-inq-date">วันที่สะดวกเข้าชม</label>
                            {/* ★ ช่องวันที่ใช้ DatePicker เสมอ (dd/mm/yyyy พ.ศ.) ไม่ใช้ input type="date" ที่แสดงตาม locale เบราว์เซอร์
                                min = วันนี้ : นัดดูห้องย้อนหลังไม่มีความหมาย */}
                            <DatePicker
                                id="hm-inq-date"
                                type="th"
                                min={todayIso()}
                                value={form.visit_date}
                                onChange={(value) => this.set("visit_date", value)}
                            />
                        </div>
                        <div className="hm-form__row">
                            <label htmlFor="hm-inq-time">เวลา</label>
                            <input id="hm-inq-time" type="time" value={form.visit_time} onChange={(e) => this.set("visit_time", e.target.value)} />
                        </div>
                    </div>
                    <div className="hm-form__row">
                        <label htmlFor="hm-inq-message">ข้อความเพิ่มเติม</label>
                        <textarea id="hm-inq-message" rows={3} value={form.message} onChange={(e) => this.set("message", e.target.value)} />
                    </div>

                    <button type="button" className="hm-btn hm-btn--primary w-100" disabled={sending || preview} onClick={this.send}>
                        {preview ? "ส่งไม่ได้ในหน้าตัวอย่าง" : sending ? "กำลังส่ง..." : "ส่งข้อมูล"}
                    </button>
                </div>
            </div>
        );
    }
}
